import React, {
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  collection,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";

import "./LeagueTabs.css";


const TIMEZONE = "Africa/Dar_es_Salaam";

const FIFTEEN_MINUTES = 15 * 60 * 1000;


/* =========================================================
   COUNTRY / LEAGUE FLAGS
========================================================= */

const LEAGUE_FLAGS = {
  "premier league": "🏴",
  "english premier league": "🏴",
  "england": "🏴",

  "la liga": "🇪🇸",
  "spain": "🇪🇸",
  "spanish la liga": "🇪🇸",

  "serie a": "🇮🇹",
  "italy": "🇮🇹",
  "italian serie a": "🇮🇹",

  "bundesliga": "🇩🇪",
  "germany": "🇩🇪",
  "german bundesliga": "🇩🇪",

  "ligue 1": "🇫🇷",
  "france": "🇫🇷",
  "french ligue 1": "🇫🇷",

  "uefa champions league": "🇪🇺",
  "champions league": "🇪🇺",

  "uefa europa league": "🇪🇺",
  "europa league": "🇪🇺",

  "uefa conference league": "🇪🇺",
  "conference league": "🇪🇺",

  "saudi pro league": "🇸🇦",
  "saudi arabia": "🇸🇦",

  "tanzania": "🇹🇿",
  "tanzanian premier league": "🇹🇿",

  "kenya": "🇰🇪",
  "kenyan premier league": "🇰🇪",

  "uganda": "🇺🇬",
  "uganda premier league": "🇺🇬",

  "south africa": "🇿🇦",
  "egypt": "🇪🇬",
  "morocco": "🇲🇦",
  "spain": "🇪🇸",
  "portugal": "🇵🇹",
  "netherlands": "🇳🇱",
  "belgium": "🇧🇪",
  "turkey": "🇹🇷",
  "brazil": "🇧🇷",
  "argentina": "🇦🇷",
  "usa": "🇺🇸",
  "united states": "🇺🇸",
  "mexico": "🇲🇽",
  "japan": "🇯🇵",
  "south korea": "🇰🇷",
  "china": "🇨🇳",
};


/* =========================================================
   GET FLAG
========================================================= */

const getLeagueFlag = (leagueName) => {
  if (!leagueName) {
    return "🌍";
  }

  const normalizedName = String(
    leagueName
  )
    .trim()
    .toLowerCase();

  if (LEAGUE_FLAGS[normalizedName]) {
    return LEAGUE_FLAGS[normalizedName];
  }

  if (
    normalizedName.includes("premier league") &&
    normalizedName.includes("england")
  ) {
    return "🏴";
  }

  if (normalizedName.includes("la liga")) {
    return "🇪🇸";
  }

  if (normalizedName.includes("serie a")) {
    return "🇮🇹";
  }

  if (normalizedName.includes("bundesliga")) {
    return "🇩🇪";
  }

  if (normalizedName.includes("ligue 1")) {
    return "🇫🇷";
  }

  if (normalizedName.includes("champions league")) {
    return "🇪🇺";
  }

  if (normalizedName.includes("europa league")) {
    return "🇪🇺";
  }

  if (normalizedName.includes("saudi")) {
    return "🇸🇦";
  }

  if (normalizedName.includes("tanzania")) {
    return "🇹🇿";
  }

  if (normalizedName.includes("kenya")) {
    return "🇰🇪";
  }

  if (normalizedName.includes("uganda")) {
    return "🇺🇬";
  }

  return "🌍";
};


/* =========================================================
   DATE + TIME
========================================================= */

const parseDate = (
  date,
  time = ""
) => {
  if (!date) {
    return 0;
  }

  try {
    /* Firestore Timestamp */

    if (
      typeof date === "object" &&
      typeof date.toDate === "function"
    ) {
      const value = date.toDate().getTime();

      return Number.isNaN(value)
        ? 0
        : value;
    }


    /* JavaScript Date */

    if (date instanceof Date) {
      const value = date.getTime();

      return Number.isNaN(value)
        ? 0
        : value;
    }


    /* Firestore timestamp object */

    if (
      typeof date === "object" &&
      typeof date.seconds === "number"
    ) {
      const value =
        Number(date.seconds) * 1000;

      return Number.isNaN(value)
        ? 0
        : value;
    }


    const dateString = String(
      date
    ).trim();

    if (!dateString) {
      return 0;
    }


    /*
      Database example:

      date = 2026-09-02
      time = 23:00

      Tanzania = UTC+3
    */

    if (
      /^\d{4}-\d{2}-\d{2}$/.test(
        dateString
      )
    ) {
      let cleanTime = String(
        time || "00:00"
      ).trim();

      if (
        /^\d{1,2}:\d{2}$/.test(
          cleanTime
        )
      ) {
        const [
          hour,
          minute,
        ] = cleanTime.split(":");

        cleanTime = `${String(
          Number(hour)
        ).padStart(2, "0")}:${minute}`;
      } else {
        cleanTime = "00:00";
      }

      const iso =
        `${dateString}T${cleanTime}:00+03:00`;

      const value = new Date(
        iso
      ).getTime();

      return Number.isNaN(value)
        ? 0
        : value;
    }


    /*
      Kama date tayari ina datetime
    */

    const cleanDate = dateString.replace(
      " ",
      "T"
    );

    const value = new Date(
      cleanDate
    ).getTime();

    return Number.isNaN(value)
      ? 0
      : value;

  } catch (error) {
    console.error(
      "LEAGUE DATE ERROR:",
      error
    );

    return 0;
  }
};


/* =========================================================
   LEAGUE FLAG COMPONENT
========================================================= */

const LeagueFlag = ({
  name,
}) => {
  const flag = getLeagueFlag(name);

  return (
    <div
      className="league-flag-box"
      aria-label={`${name} flag`}
    >
      <span className="league-flag">
        {flag}
      </span>
    </div>
  );
};


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function LeagueTabs() {
  const navigate = useNavigate();

  const [
    leagues,
    setLeagues,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     OPEN LEAGUE
  ======================================================= */

  const openLeague = (
    leagueName
  ) => {
    if (!leagueName) {
      return;
    }

    navigate(
      `/league/${encodeURIComponent(
        leagueName
      )}`
    );
  };


  /* =======================================================
     LOAD LEAGUES
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadLeagues = async () => {
      try {
        setLoading(true);

        const snapshot = await getDocs(
          collection(
            db,
            "matches"
          )
        );

        const matches = snapshot.docs.map(
          (doc) => ({
            id: doc.id,
            ...doc.data(),
          })
        );

        const now = Date.now();

        const groupedLeagues = {};


        matches.forEach((match) => {
          if (!match.date) {
            return;
          }

          const matchTime = parseDate(
            match.date,
            match.time
          );

          if (!matchTime) {
            return;
          }


          /*
            Ondoa mechi ambazo zimeanza
            au ziko ndani ya dakika 15
          */

          if (
            matchTime - now <=
            FIFTEEN_MINUTES
          ) {
            return;
          }


          const leagueName = String(
            match.league || "Other"
          ).trim();

          if (!leagueName) {
            return;
          }


          if (
            !groupedLeagues[
              leagueName
            ]
          ) {
            groupedLeagues[
              leagueName
            ] = {
              name: leagueName,
              count: 0,
            };
          }


          groupedLeagues[
            leagueName
          ].count += 1;
        });


        if (!mounted) {
          return;
        }


        const result = Object.values(
          groupedLeagues
        ).sort(
          (a, b) =>
            a.name.localeCompare(
              b.name,
              undefined,
              {
                sensitivity: "base",
              }
            )
        );

        setLeagues(result);

      } catch (error) {
        console.error(
          "LEAGUE LOAD ERROR:",
          error
        );

        if (mounted) {
          setLeagues([]);
        }

      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };


    loadLeagues();


    return () => {
      mounted = false;
    };
  }, []);


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="league-loading-screen">
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }


  /* =======================================================
     EMPTY
  ======================================================= */

  if (leagues.length === 0) {
    return (
      <div className="league-empty">
        No leagues found
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <section className="league-section">
      <div className="league-wrapper">
        <div className="league-track">
          {leagues.map((league) => (
            <div
              key={league.name}
              className="league-card"
              role="button"
              tabIndex={0}
              title={`Open ${league.name}`}
              onClick={() =>
                openLeague(
                  league.name
                )
              }
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  event.preventDefault();

                  openLeague(
                    league.name
                  );
                }
              }}
            >
              <LeagueFlag
                name={league.name}
              />

              <div className="league-card-name">
                {league.name}
              </div>

              <div className="league-count">
                {league.count}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}